export const RECORDING_CANDIDATE_KEY = 'recordingCandidateKey'
export const RECORDING_GROUP_ID = 'recordingGroupId'

export interface RecordingTarget {
  recordingCandidateKey?: string
  recordingGroupId?: string
}

interface RecordingParticipant {
  identity?: string
  isAgent?: boolean
  attributes?: Record<string, string>
  permissions?: {
    agent?: boolean
    recorder?: boolean
    hidden?: boolean
    canPublish?: boolean
    canSubscribe?: boolean
  }
}

interface RoomCompositeEgressDescriptor {
  roomId?: string
  request?: {
    case?: string
    value?: unknown
  }
}

type ParticipantGroupMap = Record<string, string | null | undefined>

const normalizeScalar = (value: unknown) => {
  if (typeof value !== 'string') return undefined

  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

export const readRecordingTarget = (source?: Record<string, unknown>): RecordingTarget => ({
  recordingCandidateKey: normalizeScalar(source?.[RECORDING_CANDIDATE_KEY]),
  recordingGroupId: normalizeScalar(source?.[RECORDING_GROUP_ID]),
})

export const extractRecordingCandidateKey = (egress: RoomCompositeEgressDescriptor) => {
  if (egress.request?.case !== 'roomComposite') return undefined

  const customBaseUrl = (egress.request.value as { customBaseUrl?: unknown } | undefined)
    ?.customBaseUrl
  if (typeof customBaseUrl !== 'string' || !customBaseUrl) return undefined

  try {
    return readRecordingTarget(
      Object.fromEntries(new URL(customBaseUrl, 'http://localhost').searchParams),
    ).recordingCandidateKey
  } catch {
    return undefined
  }
}

export const getParticipantCandidateKey = (participant: RecordingParticipant) => {
  const phone = normalizeScalar(participant.attributes?.phone)
  if (!phone) return undefined

  const digits = phone.replace(/\D/g, '')
  return digits.length > 0 ? `candidate-${digits}` : undefined
}

export const getTargetedRecorderIdentity = (recordingCandidateKey: string) =>
  `recorder-${recordingCandidateKey}`

export const getEffectiveRecordingGroupId = (
  participant: RecordingParticipant,
  participantGroupMap?: ParticipantGroupMap,
) => {
  if (
    participant.identity &&
    participantGroupMap &&
    Object.prototype.hasOwnProperty.call(participantGroupMap, participant.identity)
  ) {
    return normalizeScalar(participantGroupMap[participant.identity])
  }

  return normalizeScalar(participant.attributes?.groupId)
}

export const shouldIncludeParticipantInTargetRecording = (
  participant: RecordingParticipant,
  target: RecordingTarget,
  participantGroupMap?: ParticipantGroupMap,
) => {
  if (
    !target.recordingCandidateKey ||
    participant.isAgent ||
    participant.permissions?.agent ||
    participant.permissions?.recorder ||
    participant.permissions?.hidden ||
    participant.identity?.startsWith('agent-') ||
    (participant.permissions?.canPublish === false &&
      participant.permissions?.canSubscribe === false)
  ) {
    return false
  }

  const isStaff =
    participant.attributes?.isAdmin === 'true' ||
    participant.attributes?.isCurator === 'true'
  if (isStaff) {
    const participantGroupId = getEffectiveRecordingGroupId(participant, participantGroupMap)

    return (
      !target.recordingGroupId ||
      !participantGroupId ||
      participantGroupId === target.recordingGroupId
    )
  }

  const participantCandidateKey = getParticipantCandidateKey(participant)
  if (participantCandidateKey) {
    return participantCandidateKey === target.recordingCandidateKey
  }

  if (!target.recordingGroupId) {
    return true
  }

  return (
    getEffectiveRecordingGroupId(participant, participantGroupMap) === target.recordingGroupId
  )
}
